import React, { useMemo, useRef, useState } from 'react';
import {
  Backdrop,
  Box,
  Button,
  Container,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ShareIcon from '@mui/icons-material/Share';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CharacterDetailModal from './CharacterDetailModal';

function possessionLabel(state) {
  if (state === 'possessed') {
    return '所持のみ';
  }
  if (state === 'notPossessed') {
    return '未所持のみ';
  }
  return '全て';
}

function filterLabel(key) {
  if (key === 'role') return 'タイプ';
  if (key === 'element') return '属性';
  if (key === 'race') return '陣営';
  if (key === 'affiliation') return '所属';
  return key;
}

function resolveFilterIconName(filterKey, value) {
  const raw = String(value || '').trim();

  if (filterKey === 'role') {
    if (raw === 'ヒール' || raw === 'HEAL') return 'HEAL';
    if (raw === 'アタック' || raw === 'ATK') return 'ATK';
    if (raw === 'ディフェンス' || raw === 'DEF') return 'DEF';
    if (raw === 'スピード' || raw === 'SPD') return 'SPD';
    if (raw === 'サポート' || raw === 'SUP') return 'SUP';
  }

  if (filterKey === 'affiliation') {
    if (raw === '流星学園附属' || raw === '流星学園付属') return '流星学園付属';
  }

  return raw;
}

function FilterOptionButton({ filterKey, value, selected, onClick }) {
  const [imgError, setImgError] = useState(false);
  const iconName = resolveFilterIconName(filterKey, value);
  const iconPath = `/img/${iconName}.png`;

  return (
    <Button
      variant={selected ? 'contained' : 'outlined'}
      size="small"
      onClick={onClick}
      sx={{ minWidth: 44, px: 0.8 }}
      title={`${filterLabel(filterKey)}: ${value}`}
    >
      {!imgError ? (
        <Box
          component="img"
          src={iconPath}
          alt={value}
          onError={() => setImgError(true)}
          sx={{ width: 22, height: 22, objectFit: 'contain' }}
        />
      ) : (
        value
      )}
    </Button>
  );
}

export default function CatalogPage({
  title,
  state,
  onTogglePossession,
  onSetRarity,
  rarityOptions,
  renderRarityControl,
  onShare,
  interactive = true,
  renderMainImage,
  renderOverlay,
}) {
  const screenshotRef = useRef(null);
  const [selected, setSelected] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [renderExport, setRenderExport] = useState(false);
  const EXPORT_WIDTH = 1920;
  const EXPORT_HEIGHT = 1080;
  const EXPORT_PADDING = 24;
  const EXPORT_GAP = 6;

  const exportLayout = useMemo(() => {
    const count = Math.max(1, state.items.length);
    const innerWidth = EXPORT_WIDTH - EXPORT_PADDING * 2;
    const innerHeight = EXPORT_HEIGHT - EXPORT_PADDING * 2;

    let columns = Math.max(1, Math.ceil(Math.sqrt((count * innerWidth) / innerHeight)));
    let rows = Math.max(1, Math.ceil(count / columns));

    let cellWidth = (innerWidth - EXPORT_GAP * (columns - 1)) / columns;
    let cellHeight = (innerHeight - EXPORT_GAP * (rows - 1)) / rows;
    let cellSize = Math.max(24, Math.floor(Math.min(cellWidth, cellHeight)));

    columns = Math.max(1, Math.floor((innerWidth + EXPORT_GAP) / (cellSize + EXPORT_GAP)));
    rows = Math.max(1, Math.ceil(count / columns));
    cellWidth = (innerWidth - EXPORT_GAP * (columns - 1)) / columns;
    cellHeight = (innerHeight - EXPORT_GAP * (rows - 1)) / rows;
    cellSize = Math.max(24, Math.floor(Math.min(cellWidth, cellHeight)));

    return { columns, cellSize };
  }, [state.items.length]);
  const normalizedRarityOptions = rarityOptions.map((option) => {
    if (option && typeof option === 'object' && 'value' in option) {
      return option;
    }
    return { value: option, label: String(option) };
  });

  const handleDownload = async () => {
    if (isSaving) {
      return;
    }
    setIsSaving(true);
    setRenderExport(true);
    try {
      await new Promise((resolve) => requestAnimationFrame(resolve));
      await new Promise((resolve) => requestAnimationFrame(resolve));

      const node = screenshotRef.current;
      if (!node) {
        return;
      }
      const { toJpeg } = await import('html-to-image');

      const images = Array.from(node.querySelectorAll('img'));
      await Promise.all(
        images.map(
          (img) =>
            new Promise((resolve) => {
              if (img.complete && img.naturalWidth > 0) {
                resolve();
                return;
              }
              const done = () => {
                img.removeEventListener('load', done);
                img.removeEventListener('error', done);
                resolve();
              };
              img.addEventListener('load', done, { once: true });
              img.addEventListener('error', done, { once: true });
              setTimeout(done, 2000);
            })
        )
      );

      const dataUrl = await toJpeg(node, {
        backgroundColor: '#0f172a',
        quality: 0.92,
        pixelRatio: 1,
        width: EXPORT_WIDTH,
        height: EXPORT_HEIGHT,
        canvasWidth: EXPORT_WIDTH,
        canvasHeight: EXPORT_HEIGHT,
      });

      const response = await fetch(dataUrl);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `${title}.jpg`;
      link.click();
      URL.revokeObjectURL(objectUrl);
    } finally {
      setRenderExport(false);
      setIsSaving(false);
    }
  };

  const handleSortClick = (key) => {
    if (state.sortBy === key) {
      state.setSortOrder(state.sortOrder === 'asc' ? 'desc' : 'asc');
      return;
    }
    state.setSortBy(key);
    state.setSortOrder('asc');
  };

  const renderSortIcon = (key) => {
    if (state.sortBy !== key) {
      return null;
    }
    return state.sortOrder === 'asc' ? (
      <ArrowUpwardIcon sx={{ ml: 0.5, fontSize: 16 }} />
    ) : (
      <ArrowDownwardIcon sx={{ ml: 0.5, fontSize: 16 }} />
    );
  };

  return (
    <Container sx={{ py: 2 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        {title}
      </Typography>

      <TextField
        fullWidth
        label="キャラクター検索"
        margin="normal"
        value={state.searchText}
        onChange={(e) => state.setSearchText(e.target.value)}
      />

      <TextField
        fullWidth
        label="スキル/装飾品検索"
        margin="normal"
        value={state.skillSearchText || ''}
        onChange={(e) => state.setSkillSearchText(e.target.value)}
      />

      <Box sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, backgroundColor: 'background.paper' }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          フィルター
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
          {Object.entries(state.filterOptions).map(([key, values]) => (
            <Box key={key} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.8 }}>
              <Typography
                variant="caption"
                sx={{ minWidth: 48, pt: 0.6, color: 'text.secondary', fontWeight: 700 }}
              >
                {filterLabel(key)}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, flex: 1 }}>
                {values.map((value) => {
                  const selected = state.filters[key].includes(value);
                  return (
                    <FilterOptionButton
                      key={`${key}-${value}`}
                      filterKey={key}
                      value={value}
                      selected={selected}
                      onClick={() => state.toggleFilter(key, value)}
                    />
                  );
                })}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, backgroundColor: 'background.paper' }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          ソート・表示
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button
            variant={state.sortBy === 'implementation' ? 'contained' : 'outlined'}
            onClick={() => handleSortClick('implementation')}
          >
            実装順
            {renderSortIcon('implementation')}
          </Button>
          <Button
            variant={state.sortBy === 'name' ? 'contained' : 'outlined'}
            onClick={() => handleSortClick('name')}
          >
            名前順
            {renderSortIcon('name')}
          </Button>
          <Button variant="outlined" onClick={state.cyclePossessionFilter}>
            {possessionLabel(state.possessionFilter)}
          </Button>
          <Button variant="contained" onClick={handleDownload} disabled={isSaving}>
            {isSaving ? <CircularProgress size={20} color="inherit" /> : <FileDownloadIcon />}
          </Button>
          {onShare ? (
            <Button variant="outlined" onClick={onShare} startIcon={<ShareIcon />}>
              共有リンク
            </Button>
          ) : null}
        </Box>
      </Box>

      <Grid container spacing={1}>
        {state.items.map((item) => {
          const rarityValue = item.possessed ? item.rarity ?? '' : '';

          return (
            <Grid item key={item.id} xs={3} sm={2} md={1.5} lg={1}>
              <Box
                sx={{
                  borderRadius: 2,
                  overflow: 'hidden',
                  bgcolor: 'rgba(30, 41, 59, 0.5)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.15)',
                  }
                }}
              >
                <Box sx={{ position: 'relative' }}>
                  <Box
                    onClick={interactive ? () => onTogglePossession(item.id) : undefined}
                    sx={{ cursor: interactive ? 'pointer' : 'default', display: 'block' }}
                  >
                    {renderMainImage(item)}
                    <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '35%', background: 'linear-gradient(to top, rgba(15,23,42,0.9), transparent)', pointerEvents: 'none' }} />

                  </Box>
                  {renderOverlay ? renderOverlay(item) : null}
                  <IconButton
                    size="small"
                    onClick={() => setSelected(item)}
                    sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(0, 0, 0, 0.6)', color: '#fff', '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.8)' }, width: 26, height: 26 }}
                  >
                    <ArticleIcon sx={{ fontSize: '1.1rem' }} />
                  </IconButton>
                </Box>

                <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', bgcolor: 'rgba(15, 23, 42, 0.95)', px: 0.5 }}>
                  {renderRarityControl ? (
                    renderRarityControl(item, onSetRarity, { interactive })
                  ) : (
                    <Box sx={{ px: 1, pb: 1 }}>
                      <Select
                        size="small"
                        fullWidth
                        disabled={!item.possessed || !interactive}
                        value={rarityValue}
                        displayEmpty
                        onChange={(e) => onSetRarity(item.id, e.target.value)}
                        sx={{ mt: 1 }}
                      >
                        <MenuItem value="">
                          <em>未所持</em>
                        </MenuItem>
                        {normalizedRarityOptions.map((option) => (
                          <MenuItem key={String(option.value)} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>

      {renderExport ? (
        <Box
          ref={screenshotRef}
          sx={{
            position: 'fixed',
            left: 0,
            top: 0,
            width: `${EXPORT_WIDTH}px`,
            height: `${EXPORT_HEIGHT}px`,
            overflow: 'hidden',
            pointerEvents: 'none',
            zIndex: -1,
            background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: '100%',
              p: `${EXPORT_PADDING}px`,
              boxSizing: 'border-box',
              display: 'grid',
              gap: `${EXPORT_GAP}px`,
              justifyContent: 'center',
              alignContent: 'start',
              gridTemplateColumns: `repeat(${exportLayout.columns}, ${exportLayout.cellSize}px)`,
              gridAutoRows: `${exportLayout.cellSize}px`,
            }}
          >
            {state.items.map((item) => (
              <Box
                key={`export-${item.id}`}
                sx={{
                  width: `${exportLayout.cellSize}px`,
                  height: `${exportLayout.cellSize}px`,
                  borderRadius: 1,
                  overflow: 'hidden',
                  border: '1px solid rgba(255,255,255,0.12)',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.35)',
                  bgcolor: '#0b1220',
                }}
              >
                {renderMainImage(item)}
              </Box>
            ))}
          </Box>
        </Box>
      ) : null}

      <CharacterDetailModal
        open={Boolean(selected)}
        character={selected}
        onClose={() => setSelected(null)}
      />

      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: '#0f172a',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
        transitionDuration={0}
        open={isSaving}
      >
        <CircularProgress color="inherit" />
        <Typography variant="h6">画像を保存中...</Typography>
      </Backdrop>
    </Container>
  );
}
